import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaTooth, FaPhoneAlt, FaMapMarkerAlt } from 'react-icons/fa';
import clsx from 'clsx';
import './Header.css'; // Clearing this file next

const Header = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [pendingScroll, setPendingScroll] = useState(null);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setMobileMenuOpen(false);
        
        // Handle pending scroll after navigation to home page
        if (pendingScroll && location.pathname === '/') {
            setTimeout(() => {
                const element = document.getElementById(pendingScroll);
                if (element) {
                    const headerOffset = 100;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth"
                    });
                    setPendingScroll(null);
                }
            }, 200);
        }
        
        // Handle hash navigation from URL
        const hash = window.location.hash;
        if (hash && location.pathname === '/') {
            const id = hash.replace('#', '');
            setTimeout(() => {
                const element = document.getElementById(id);
                if (element) {
                    const headerOffset = 100;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth"
                    });
                }
            }, 100);
        }
    }, [location, pendingScroll]);

    const navLinks = [
        { name: 'Home', path: '/', type: 'link' },
        { name: 'Services', path: '/#services', type: 'anchor' },
        { name: 'About Us', path: '/#about-us', type: 'anchor' },
        { name: 'Contact', path: '/contact', type: 'link' },
    ];

    const scrollToSection = (e, path) => {
        e.preventDefault();
        
        if (path.startsWith('/#')) {
            const id = path.replace('/#', '');
            
            // If we're not on the home page, navigate first and set pending scroll
            if (location.pathname !== '/') {
                setPendingScroll(id);
                navigate('/');
            } else {
                // We're already on home page, scroll directly
                const performScroll = () => {
                    const element = document.getElementById(id);
                    if (element) {
                        const headerOffset = 100;
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                        
                        window.scrollTo({
                            top: Math.max(0, offsetPosition),
                            behavior: "smooth"
                        });
                        
                        // Update URL hash
                        window.history.replaceState(null, '', `/#${id}`);
                        return true;
                    }
                    return false;
                };
                
                // Try scrolling immediately
                if (!performScroll()) {
                    // If element not found, try with delays (for mobile menu animation)
                    setTimeout(() => {
                        if (!performScroll()) {
                            setTimeout(performScroll, 200);
                        }
                    }, 100);
                }
            }
        }
    };

    return (
        <header
            className={clsx(
                'fixed top-0 left-0 right-0 z-[100] transition-all duration-300',
                scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-2' : 'bg-transparent py-4'
            )}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 group">
                        <div className="bg-primary-600 text-white p-2 rounded-lg shadow-lg group-hover:scale-105 transition-transform">
                            <FaTooth className="text-xl" />
                        </div>
                        <div className="flex flex-col">
                            <span className={clsx("text-xl font-bold leading-tight", scrolled ? "text-gray-900" : "text-gray-900")}>
                                Wisdom Dental
                            </span>
                            <span className="text-xs text-primary-600 font-medium tracking-wider">CLINIC</span>
                            <span className={clsx("text-xs mt-0.5 flex items-center gap-1 hidden sm:flex", scrolled ? "text-gray-600" : "text-gray-600")}>
                                <FaMapMarkerAlt className="text-[10px]" />
                                Sector 23, Gurugram 122017
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            link.type === 'anchor' ? (
                                <a
                                    key={link.name}
                                    href={link.path}
                                    onClick={(e) => scrollToSection(e, link.path)}
                                    className="text-sm font-semibold text-gray-700 hover:text-primary-600 transition-colors uppercase tracking-wide"
                                >
                                    {link.name}
                                </a>
                            ) : (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className="text-sm font-semibold text-gray-700 hover:text-primary-600 transition-colors uppercase tracking-wide"
                                >
                                    {link.name}
                                </Link>
                            )
                        ))}
                    </nav>

                    {/* CTA Button */}
                    <div className="hidden md:flex items-center gap-4">
                        <a
                            href="tel:+918076353837"
                            className="flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium transition-colors"
                        >
                            <FaPhoneAlt className="text-sm" />
                            <span>+91 80763 53837</span>
                        </a>
                        <Link
                            to="/contact"
                            className="bg-primary-600 text-white px-6 py-2.5 rounded-full font-semibold shadow-lg shadow-primary-600/20 hover:bg-primary-700 hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm"
                        >
                            Book Appointment
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="md:hidden text-gray-700 p-2"
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden bg-white border-t border-gray-100 overflow-hidden shadow-xl"
                    >
                        <div className="px-4 py-6 space-y-4">
                            {navLinks.map((link) => (
                                link.type === 'anchor' ? (
                                    <a
                                        key={link.name}
                                        href={link.path}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const sectionId = link.path.replace('/#', '');
                                            setMobileMenuOpen(false);
                                            // Wait for menu animation to complete before scrolling
                                            setTimeout(() => {
                                                // Create a synthetic event for scrollToSection
                                                const syntheticEvent = { preventDefault: () => {} };
                                                scrollToSection(syntheticEvent, link.path);
                                            }, 350);
                                        }}
                                        className="block text-lg font-medium text-gray-800 hover:text-primary-600"
                                    >
                                        {link.name}
                                    </a>
                                ) : (
                                    <Link
                                        key={link.name}
                                        to={link.path}
                                        className="block text-lg font-medium text-gray-800 hover:text-primary-600"
                                    >
                                        {link.name}
                                    </Link>
                                )
                            ))}
                            <div className="pt-4 border-t border-gray-100">
                                <Link
                                    to="/contact"
                                    className="block w-full text-center bg-primary-600 text-white px-6 py-3 rounded-lg font-bold shadow-md active:scale-95 transition-transform"
                                >
                                    Book Appointment
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
